import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { api } from '@/utils/api';
import { EyeIcon } from '@/ui/icons';

type Announcement = {
  id: number;
  title: string;
  slug: string;
  excerpt?: string;
  publishedAt?: string;
  viewCount?: number;
};

export function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 12;

  useEffect(() => {
    document.title = 'Duyurular | ESDFL MUN';
    let ignore = false;
    (async () => {
      try {
        const res = await api.get(`/api/public/announcements?limit=${PAGE_SIZE}&offset=0`);
        if (!ignore) {
          setAnnouncements(res.announcements ?? []);
          setHasMore(!!res.hasMore);
          setOffset(res.announcements?.length ?? 0);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get(`/api/public/announcements?limit=${PAGE_SIZE}&offset=${offset}`);
      const newItems: Announcement[] = res.announcements ?? [];
      setAnnouncements((a) => [...a, ...newItems]);
      setOffset(offset + newItems.length);
      setHasMore(!!res.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm transition-colors duration-300">
      <Helmet>
        <title>Duyurular | ESDFL MUN</title>
        <meta name="description" content="ESDFL MUN duyuruları" />
      </Helmet>

      <h1 className="text-2xl md:text-3xl font-serif font-bold mb-6 text-gray-900 dark:text-white">Duyurular</h1>

      {loading && <div className="text-gray-600 dark:text-gray-400">Yükleniyor…</div>}
      
      {!loading && announcements.length === 0 && (
        <div className="card p-8 text-center dark:bg-gray-700">
          <p className="text-gray-600 dark:text-gray-400">Henüz duyuru yok.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {announcements.map((ann) => (
          <Link 
            to={`/duyuru/${ann.slug}`} 
            key={ann.id} 
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 p-4 hover:shadow-md transition"
          >
            <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white">{ann.title}</h2>
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-2">
              {ann.publishedAt && (
                <span>{new Date(ann.publishedAt).toLocaleDateString('tr-TR')}</span>
              )}
              <span className="inline-flex items-center gap-1"><EyeIcon /> {ann.viewCount ?? 0}</span>
            </div>
            {ann.excerpt && (
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">{ann.excerpt}</p>
            )}
          </Link>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-6">
          <button className="btn" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Yükleniyor…' : 'Daha fazla göster'}
          </button>
        </div>
      )}
    </div>
  );
}
